// ---------------------------------------------------------------------------
// "Download my turf" (PLAN 7.5, PLAN 8, PLAN 12 phase 5).
//
// One tap on wifi before a shift, and the turf works at every door afterwards.
// It readies three things:
//
//   * persistent storage, so a week of disuse does not evict the queue,
//   * the basemap archive, so the map draws with no signal,
//   * a sync, which pulls this turf's households into the device store.
//
// The archive covers the whole city rather than one turf's bounding box --
// PLAN 7.5 assumed it was hosted on R2 and fetched in byte ranges, but it now
// ships with the app at 2.9 MB, and subsetting 2.9 MB per turf would cost more
// in complexity than it saves on the wire. So downloading any one turf readies
// the map for all of them; only the household rows are per-turf.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { cacheBasemap } from '../offline/basemap';
import { requestPersistence } from '../offline/storage';
import { syncNow } from '../offline/sync';

interface Props {
  /** Households already on the device for this turf. Zero means nothing to walk. */
  householdCount: number;
  basemapReady: boolean | null;
  onBasemapCached: (cached: boolean) => void;
}

export function TurfDownload({ householdCount, basemapReady, onBasemapCached }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = basemapReady === true && householdCount > 0;

  async function download(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      // Best-effort and deliberately unchecked: a browser that declines still
      // stores everything, it just makes no promise about keeping it.
      await requestPersistence();

      const cached = await cacheBasemap();
      onBasemapCached(cached);
      if (!cached) throw new Error('The map could not be downloaded.');

      await syncNow();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'Download failed. Try again while you have wifi.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="download">
      <span className={`download__state${ready ? ' download__state--ready' : ''}`}>
        {busy
          ? 'Downloading…'
          : ready
            ? 'Ready to walk offline'
            : 'Not downloaded'}
      </span>
      <button
        className="btn btn--secondary btn--small"
        disabled={busy}
        onClick={() => void download()}
      >
        {ready ? 'Refresh' : 'Download'}
      </button>
      {error ? <p className="download__error">{error}</p> : null}
    </div>
  );
}
